// Display rules for the Security Operations CVE section (/soc). Every figure comes from the summary's
// `vulnerabilities` object, which the backend counts once for the Overview and the Endpoints view;
// nothing here recounts, it only words, sorts and labels.
import { STALE_AFTER_HOURS, formatLocalDate, parseUtc } from "./socFreshness";

const HOUR_MS = 60 * 60 * 1000;
// The sync reads devices Defender rates medium or high on risk (alerts) or exposure (vulnerabilities).
const SCOPE = "devices Defender rates medium or high for risk or exposure";
const LOW_NOT_COLLECTED = "Devices low on both, and low-severity CVEs, are not collected.";

export const SEVERITY_RANK = { Critical: 4, High: 3, Medium: 2, Low: 1 };
const rank = (severity) => SEVERITY_RANK[severity] || 0;

// "Critical" -> "critical", the key SeverityBadge and the severity colours use.
export const severityKey = (severity) => (SEVERITY_RANK[severity] ? severity.toLowerCase() : "unrated");

// What the CVE figures cover, from the last vulnerability sync. Never implies the whole organization.
export const coverageLine = (coverage) => {
  const assessed = coverage?.devicesAssessed;
  const eligible = coverage?.devicesEligible;
  const cap = coverage?.deviceCap || 50;
  if (assessed == null) {
    return `Based on up to ${cap} ${SCOPE}. ${LOW_NOT_COLLECTED}`;
  }
  if (eligible == null) {
    return `Based on ${assessed} of the ${SCOPE}; `
      + `the full list of those devices couldn't be read. ${LOW_NOT_COLLECTED}`;
  }
  const capNote = eligible > assessed && eligible > cap ? ` Axaris reads up to ${cap} per sync.` : "";
  return `Based on ${assessed} of the ${eligible} ${SCOPE}.${capNote} ${LOW_NOT_COLLECTED}`;
};

// The saved device counts, when they can describe the CVEs on screen; else the counts left out, so the
// line falls back to the fixed-rule wording. They can't when no device was assessed although CVEs are
// listed, or when they were saved well before the Defender XDR sync that produced the current figures
// (a sync that didn't record coverage, such as one run by an older version of Axaris).
const COVERAGE_LAG_MS = 6 * HOUR_MS;
export const usableCoverage = (coverage, instances, lastSync) => {
  if (!coverage) return coverage;
  const at = parseUtc(coverage.at);
  const contradicts = coverage.devicesAssessed === 0 && (Number(instances) || 0) > 0;
  const older = !!at && !!lastSync && lastSync.getTime() - at.getTime() > COVERAGE_LAG_MS;
  return contradicts || older ? { deviceCap: coverage.deviceCap } : coverage;
};

// While the per-device CVE sync is switched off, the figures are the CVEs collected before that and
// nothing updates them. Reads the raw coverage (usableCoverage can drop fields from an old one).
export const collectionPaused = (coverage) => coverage?.collectionPaused === true;

export const pausedLine = (coverage) => {
  const at = parseUtc(coverage?.at);
  const since = at ? ` They were last updated on ${formatLocalDate(at)}.` : "";
  return "CVE collection is paused while it is being upgraded. These are the CVEs collected before the pause, "
    + `and they aren't being updated.${since}`;
};

// True when the figures cover fewer devices than Defender has in scope, or that number is unknown.
export const isPartialCoverage = (coverage) => {
  const assessed = coverage?.devicesAssessed;
  const eligible = coverage?.devicesEligible;
  if (assessed == null) return false; // no saved counts yet: the fixed-rule wording says enough
  return eligible == null || assessed < eligible;
};

// The Defender XDR source of the Endpoint tile: when it last synced and whether that is stale.
export const defenderSync = (endpointTile, now = new Date()) => {
  const source = (endpointTile?.sources || []).find((s) => s?.name === "Defender XDR");
  const lastSync = parseUtc(source?.lastSyncAt);
  if (!lastSync) return { lastSync: null, stale: false };
  const ageMs = Math.max(0, now.getTime() - lastSync.getTime());
  return { lastSync, ageMs, stale: ageMs > STALE_AFTER_HOURS.endpoint * HOUR_MS };
};

const firstDetectedMs = (cve) => parseUtc(cve.firstDetected)?.getTime() ?? 0;

// Defender's three exploit signals, kept apart. null = unknown, false = Defender said no.
export const EXPLOIT_SIGNALS = [
  ["publicExploit", "Public exploit"],
  ["exploitVerified", "Verified exploit"],
  ["exploitInKit", "Exploit kit"],
];
export const knownExploit = (details) => EXPLOIT_SIGNALS.some(([k]) => details?.[k] === true);
const cvssOf = (cve) => cve.details?.cvssScore ?? null;
const publishedMs = (cve) => parseUtc(cve.details?.publishedOn)?.getTime() ?? null;

// A missing value always sorts last, whichever way the column is sorted.
const nullsLast = (x, y, sign) => {
  if (x == null && y == null) return 0;
  if (x == null) return 1;
  if (y == null) return -1;
  return sign * (x - y);
};

const COMPARE = {
  severity: (a, b) => rank(a.severity) - rank(b.severity),
  cvss: (a, b) => (cvssOf(a) ?? -1) - (cvssOf(b) ?? -1),
  devices: (a, b) => a.deviceCount - b.deviceCount,
  firstDetected: (a, b) => firstDetectedMs(a) - firstDetectedMs(b),
  published: (a, b) => (publishedMs(a) ?? 0) - (publishedMs(b) ?? 0),
};
const NULLABLE = { cvss: cvssOf, published: publishedMs };

// The documented default order: highest severity, then a known exploit, then highest CVSS (none
// last), then most devices, then oldest first detected.
const defaultOrder = (a, b) =>
  -COMPARE.severity(a, b)
  || Number(knownExploit(b.details)) - Number(knownExploit(a.details))
  || nullsLast(cvssOf(a), cvssOf(b), -1)
  || -COMPARE.devices(a, b)
  || COMPARE.firstDetected(a, b)
  || a.cveId.localeCompare(b.cveId);

export const DEFAULT_SORT = { key: null, dir: "desc" };

// Sorts by the chosen column; ties fall back to the default order, so the result is always stable.
export const sortCves = (cves, sort = DEFAULT_SORT) => {
  const rows = [...(cves || [])];
  if (!sort?.key || !COMPARE[sort.key]) return rows.sort(defaultOrder);
  const sign = sort.dir === "asc" ? 1 : -1;
  const value = NULLABLE[sort.key];
  const by = value ? (a, b) => nullsLast(value(a), value(b), sign) : (a, b) => sign * COMPARE[sort.key](a, b);
  return rows.sort((a, b) => by(a, b) || defaultOrder(a, b));
};

// Filters for the table only; the figures above it always describe every open CVE.
export const CVSS_HIGH = 9;
export const filterCves = (cves, { exploitOnly = false, cvssHigh = false } = {}) =>
  (cves || []).filter((c) => (!exploitOnly || knownExploit(c.details))
    && (!cvssHigh || (cvssOf(c) != null && cvssOf(c) >= CVSS_HIGH)));

export const formatCvss = (score) => (score == null ? "Not available" : Number(score).toFixed(1));

// "3/1/2026 · public for 229 days" (local date format); "Not available" when Defender sent no date.
export const publishedText = (publishedOn, now = new Date()) => {
  const date = parseUtc(publishedOn);
  if (!date) return "Not available";
  const days = Math.max(0, Math.floor((now.getTime() - date.getTime()) / (24 * HOUR_MS)));
  return `${date.toLocaleDateString()} · public for ${days} ${days === 1 ? "day" : "days"}`;
};

const RISK_STATUS = { identified: "Open", in_progress: "In progress" };
export const riskStatusLabel = (status) => RISK_STATUS[status] || status || "Unknown";

const REMEDIATION_STATUS = {
  NotStarted: "Not started",
  InProgress: "In progress",
  UnderReview: "Under review",
  Completed: "Completed",
  Deferred: "Deferred",
  Planned: "Planned",
};
export const remediationLabel = (status) => (status ? REMEDIATION_STATUS[status] || status : "No remediation");

// ── From the org's CVE upgrade (ReadersOn): `vulnerabilities.generation` is set ─────────────────────
// The figures come from Microsoft Defender's daily vulnerability snapshot, one published snapshot at a time,
// with separate states for collection, the risk register and financial figures (each labelled on its own).

export const fromSnapshot = (v) => !!v?.generation;
export const hasFigures = (v) => !fromSnapshot(v) || v.generation.hasFigures === true;

const snapshotDate = (g) => formatLocalDate(parseUtc(g?.snapshotGeneratedAt));

export const COLLECTION_LABEL = { Starting: "Starting", Current: "Current", Delayed: "Delayed", Paused: "Paused" };

// One sentence on where the figures stand. Never a zero before the first snapshot.
export const collectionLine = (g) => {
  const date = snapshotDate(g);
  switch (g?.collectionState) {
    case "Starting":
      return `CVE collection starting. The figures appear once Defender's first vulnerability snapshot is processed.${g.collectionNote ? ` ${g.collectionNote}.` : ""}`;
    case "Paused":
      return date
        ? `CVE collection is paused. These figures are from Defender's snapshot of ${date} and aren't being updated.`
        : "CVE collection is paused.";
    case "Delayed":
      return g.collectionNote
        ? `${g.collectionNote}.${date && !g.collectionNote.includes("snapshot from") ? ` These figures are from the snapshot of ${date}.` : ""}`
        : `A newer snapshot is delayed. These figures are from Defender's snapshot of ${date}.`;
    default:
      return date ? `From Defender's vulnerability snapshot of ${date}.` : "From Defender's latest vulnerability snapshot.";
  }
};

// §9.4 changed item 1: the device count of the snapshot replaces the old per-sync cap wording.
export const snapshotCoverageLine = (coverage, g) => {
  const n = coverage?.devicesAssessed;
  const date = snapshotDate(g);
  const base = n == null
    ? "Based on Defender's vulnerability assessment"
    : `Based on Defender's vulnerability assessment of ${n} ${n === 1 ? "device" : "devices"}`;
  return `${base}${date ? ` (snapshot of ${date})` : ""}. Low-severity CVEs are not collected.`;
};

const REGISTER_LABEL = { UpToDate: "Up to date", Pending: "Updating", Failed: "Update failed" };
const FINANCIAL_LABEL = { UpToDate: "Up to date", Updating: "Updating" };
export const registerLabel = (state) => REGISTER_LABEL[state] || "Unknown";
export const financialLabel = (state) => FINANCIAL_LABEL[state] || "Unknown";

export const FIRST_DETECTED_TIP = { legacy: "When Axaris first recorded this CVE, not when it was published", snapshot: "First seen by Microsoft Defender" };

